import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Wordmark from './Wordmark';
import { useAuth } from '../contexts/AuthContext';

export default function Nav({ minimal = false }: { minimal?: boolean } = {}) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { session, role, signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll while drawer is open.
  useEffect(() => {
    if (menuOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [menuOpen]);

  async function handleSignOut() {
    setMenuOpen(false);
    await signOut();
    navigate('/');
  }

  return (
    <>
      <nav
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          background: scrolled ? 'color-mix(in oklch, var(--bg) 88%, transparent)' : 'transparent',
          backdropFilter: scrolled ? 'blur(12px)' : 'none',
          borderBottom: scrolled ? '1px solid var(--line)' : '1px solid transparent',
          transition: 'background 0.25s ease, border-color 0.25s ease, backdrop-filter 0.25s ease',
        }}
      >
        <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 18, paddingBottom: 18, gap: 12 }}>
          <Link to="/" aria-label="Goaround home">
            <Wordmark size={24} />
          </Link>

          {minimal ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Link to="/dashboard" className="btn btn-primary btn-sm">
                {role === 'platform' ? 'Admin' : role === 'vendor' ? 'Vendor' : 'Dashboard'}
              </Link>
            </div>
          ) : (
            <>
              {/* Desktop links */}
              <div className="hide-mobile" style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                <NavLink to="/how-it-works" style={linkStyle}>How it works</NavLink>
                <NavLink to="/featured-locations" style={linkStyle}>Locations</NavLink>
                <NavLink to="/onboarding" style={linkStyle}>List your spot</NavLink>
                {session ? (
                  <>
                    <Link to="/dashboard" className="btn btn-primary btn-sm">
                      {role === 'platform' ? 'Admin' : role === 'vendor' ? 'Vendor' : 'Dashboard'}
                    </Link>
                    <button
                      onClick={handleSignOut}
                      className="btn btn-ghost btn-sm"
                      style={{ border: 'none', padding: '8px 12px' }}
                    >Sign out</button>
                  </>
                ) : (
                  <>
                    <Link to="/login" style={linkStyle}>Sign in</Link>
                    <Link to="/signup" className="btn btn-primary btn-sm">Plan a social →</Link>
                  </>
                )}
              </div>

              {/* Mobile compact bar: sign-in (or dashboard), plan-a-social, hamburger */}
              <div className="show-mobile-flex" style={{ display: 'none', alignItems: 'center', gap: 10 }}>
                {session ? (
                  <Link to="/dashboard" className="btn btn-primary btn-sm" style={{ padding: '8px 12px', fontSize: 13 }}>
                    {role === 'platform' ? 'Admin' : 'Dashboard'}
                  </Link>
                ) : (
                  <>
                    <Link to="/login" style={{ ...linkStyle, fontSize: 14 }}>Sign in</Link>
                    <Link to="/locations" className="btn btn-primary btn-sm" style={{ padding: '8px 12px', fontSize: 13 }}>Plan a social →</Link>
                  </>
                )}
                <button
                  onClick={() => setMenuOpen(true)}
                  aria-label="Open menu"
                  style={{ width: 40, height: 40, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--ink)', borderRadius: 999, background: 'transparent', cursor: 'pointer', color: 'var(--ink)' }}
                >
                  <HamburgerIcon />
                </button>
              </div>
            </>
          )}
        </div>
      </nav>

      {menuOpen && (
        <div className="drawer-backdrop">
          <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 18, paddingBottom: 18 }}>
            <Link to="/" onClick={() => setMenuOpen(false)} aria-label="Goaround home">
              <Wordmark size={24} />
            </Link>
            <button
              onClick={() => setMenuOpen(false)}
              aria-label="Close menu"
              style={{ width: 40, height: 40, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--ink)', borderRadius: 999, background: 'transparent', cursor: 'pointer', color: 'var(--ink)', fontSize: 22 }}
            >×</button>
          </div>
          <div className="container" style={{ paddingTop: 32, paddingBottom: 32, flex: 1 }}>
            <Link to="/how-it-works" onClick={() => setMenuOpen(false)} className="drawer-link">How it works</Link>
            <Link to="/featured-locations" onClick={() => setMenuOpen(false)} className="drawer-link">Locations</Link>
            <Link to="/request-location" onClick={() => setMenuOpen(false)} className="drawer-link">Request a spot</Link>
            <Link to="/for-restaurants" onClick={() => setMenuOpen(false)} className="drawer-link">For restaurants / bars</Link>
            <Link to="/onboarding" onClick={() => setMenuOpen(false)} className="drawer-link">List your spot</Link>
            {session ? (
              <>
                <Link to="/dashboard" onClick={() => setMenuOpen(false)} className="drawer-link">Dashboard</Link>
                <button onClick={handleSignOut} className="drawer-link" style={{ background: 'transparent', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>Sign out</button>
              </>
            ) : (
              <Link to="/login" onClick={() => setMenuOpen(false)} className="drawer-link">Sign in</Link>
            )}
          </div>
          <div className="container" style={{ padding: '24px 0 32px' }}>
            <Link to="/signup" onClick={() => setMenuOpen(false)} className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>Plan a social →</Link>
          </div>
        </div>
      )}
    </>
  );
}

function HamburgerIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

const linkStyle = { color: 'var(--ink)', fontSize: 15, fontWeight: 500 } as const;
